import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Panel, Empty } from "@/components/PageHeader";
import { api, eur, num, errMsg } from "@/lib/api";
import { CardActions } from "./CardActions";
import { CategoryEditor } from "./CategoryEditor";

const DAYS = [["30", "30 gg"], ["90", "90 gg"], ["180", "6 mesi"], ["365", "12 mesi"]];
const it = (v) => (v ?? 0).toLocaleString("it-IT");

export function BasketTab({ cfg, saveCfg }) {
  const [days, setDays] = useState("90");
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [sel, setSel] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (!cfg.categories.length) { setD({ categories: [], universe: 0, total_spend: 0 }); return; }
    setD(null); setError(""); api.get("/cards/basket", { params: { days } }).then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  }, [cfg.categories, days]);
  const cat = d?.categories.find((c) => c.id === sel) || d?.categories[0];

  return (
    <div className="space-y-4">
      <Panel title="Categorie / reparti definiti nel CRM" testId="basket-categories"><div className="p-4"><CategoryEditor cfg={cfg} saveCfg={saveCfg} /></div></Panel>
      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="basket-error">{error}</div>}
      {!d && !error && <Skeleton className="h-64" />}
      {d && cfg.categories.length === 0 && <Panel><Empty text="Crea la prima categoria (es. Norcineria, Ortofrutta, Bevande) per vedere quante carte la acquistano." /></Panel>}
      {d && cfg.categories.length > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5">{DAYS.map(([k, l]) => <button type="button" key={k} data-testid={`basket-days-${k}`} onClick={() => setDays(k)} className={`rounded-full px-3 py-1 text-xs ${days === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>)}</div>
              <span data-testid="basket-universe">{num(d.universe)} carte attive · spesa carte {eur(d.total_spend)}</span>
            </div>
            <CardActions tab="basket" days={Number(days)} disabled={!d.categories.length} />
          </div>
          <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr] [&>*]:min-w-0">
            <Panel title="Carte per categoria" testId="basket-table-panel">
              <div className="divide-y divide-slate-100">
                {d.categories.map((c) => (
                  <div key={c.id} data-testid={`basket-cat-${c.id}`} onClick={() => setSel(c.id)} className={`cursor-pointer p-4 transition-colors hover:bg-slate-50 ${cat?.id === c.id ? "bg-blue-50/60" : ""}`}>
                    <div className="flex items-center justify-between"><span className="font-medium">{c.name}</span><span className="font-mono text-xs">{eur(c.spend)} · {it(c.share_spend)}% spesa</span></div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-blue-600" style={{ width: `${Math.min(c.share_cards, 100)}%` }} /></div>
                    <div className="mt-1.5 grid grid-cols-2 gap-x-3 text-xs text-slate-500 sm:grid-cols-4">
                      <span><b className="text-slate-800">{num(c.cards)}</b> carte ({it(c.share_cards)}%)</span><span><b className="text-slate-800">{num(c.non_buyers)}</b> non acquistano</span>
                      <span>Media <b className="text-slate-800">{eur(c.avg_per_card)}</b>/carta</span><span><b className="text-slate-800">{num(c.receipts)}</b> scontrini</span>
                    </div>
                  </div>))}
              </div>
            </Panel>
            {cat && (
              <Panel title={`Top carte · ${cat.name}`} testId="basket-top-panel">
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
                  <CardActions tab="basket" days={Number(days)} exports={false} label={`Segmento acquirenti (${num(cat.cards)})`} testId={`cards-segment-buyers-${cat.id}`} segment={{ source: "category_buyers", category_id: cat.id }} disabled={!cat.cards} />
                  <CardActions tab="basket" days={Number(days)} exports={false} label={`Segmento non acquirenti (${num(cat.non_buyers)})`} testId={`cards-segment-nonbuyers-${cat.id}`} segment={{ source: "category_non_buyers", category_id: cat.id }} disabled={!cat.non_buyers} />
                </div>
                {cat.top.length === 0 ? <Empty text="Nessuna carta ha acquistato questa categoria nel periodo." /> : cat.top.map((t, i) => (
                  <div key={t.id} data-testid={`basket-top-${t.id}`} onClick={() => nav(`/clienti/${t.id}`)} className="flex cursor-pointer items-center justify-between border-b border-slate-100 px-4 py-2 text-sm last:border-0 hover:bg-slate-50">
                    <div className="flex items-center gap-3"><span className="w-5 font-mono text-xs text-slate-400">{i + 1}</span><div><div className="font-medium">{t.name}</div><div className="text-[11px] text-slate-400">{t.city}{t.card && ` · ${t.card}`}</div></div></div>
                    <div className="text-right"><div className="font-mono text-xs">{eur(t.spend)}</div><div className="text-[11px] text-slate-400">{num(t.receipts)} scontrini</div></div>
                  </div>))}
              </Panel>)}
          </div>
        </>
      )}
    </div>
  );
}
